import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from '@mui/material';
import { Control, useForm } from 'react-hook-form';
import DateInput from '../forms/DateInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

const PREFIX = 'AcivateDialog';

const classes = {
  form: `${PREFIX}-form`,
  dialogMessage: `${PREFIX}-dialogMessage`,
};

const StyledForm = styled(Box)(({ theme }) => ({
  [`&.${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.dialogMessage}`]: {
    ...theme.typography.body1,
  },
}));

interface ActivateDialogProps {
  title: string;
  message: string;
  forActivate: boolean;
  handleOk: (start?: string, end?: string) => void;
  handleCancel: () => void;
  open: boolean;
}

export default function ActivateDialog(props: ActivateDialogProps) {
  const { t } = useTranslation('common');

  const {
    title,
    message,
    forActivate,
    handleOk,
    handleCancel,
    open,
    ...other
  } = props;

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: { StartDate: null, EndDate: null },
  });

  const yesterday = () => {
    return DateTime.now().minus({ day: 1 }).toISODate();
  };

  const onSubmit = (data: any) => {
    let start: string | undefined;
    let end: string | undefined;
    if (forActivate) {
      start = data.StartDate ? data.StartDate.toISODate() : yesterday();
      end = data.EndDate ? data.EndDate.toISODate() : 'clear';
    } else {
      end = data.EndDate ? data.EndDate.toISODate() : yesterday();
    }

    handleOk(start, end);
    formHook.reset();
  };

  const onCancel = () => {
    handleCancel();
    formHook.reset();
  };

  return (
    <Dialog disableEscapeKeyDown maxWidth="xs" open={open} {...other}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <StyledForm className={classes.form}>
            <Typography className={classes.dialogMessage}>{message}</Typography>
            {forActivate && (
              <DateInput
                label={t('common.effectiveStartDate')}
                name={'StartDate'}
                control={formHook.control as Control<any>}
                required={false}
              />
            )}
            <DateInput
              label={t('common.effectiveEndDate')}
              name={'EndDate'}
              control={formHook.control as Control<any>}
              required={false}
            />
          </StyledForm>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={formHook.handleSubmit(onSubmit)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
